import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/utils/logger';
import STabs, { BookingTab } from '@/components/ui/molecules/STabs';
import { BookingService, BookingDetailsDto } from '@/services/booking.service';
import SBookingCard from '@/components/ui/organisms/SBookingCard';
import { useCalendarSponsorships } from '@/hooks/useCalendarSponsorships';
import SLoader from '@/components/ui/atoms/SLoader';
import SInfiniteScroll from '@/components/ui/atoms/SInfiniteScroll';
import SBack from '@/components/ui/molecules/SBack';
import SDialog from '@/components/ui/molecules/SDialog';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import { FacilityType, facilityTypeTitles } from '@/types/booking.types';
import { triggerNativeRemoveFromCalendar } from '@/utils/nativeBridge';
import './AllBookings.css';

const PAGE_SIZE = 10;

// Reschedule/cancel stay available for the entire pre-start window — even
// while Check-in is already showing (it opens 30 min early) — and only
// disappear once the booking's actual start time has passed.
function hasBookingStarted(startTime: string): boolean {
  return new Date(startTime).getTime() <= Date.now();
}

function computeHours(bookings: BookingDetailsDto[]): string {
  const relevant = bookings.filter(
    (b) => b.status === 'completed' || (b.status === 'active' && new Date(b.startTime) < new Date()),
  );
  const totalMs = relevant.reduce(
    (sum, b) => sum + Math.max(0, new Date(b.endTime).getTime() - new Date(b.startTime).getTime()),
    0,
  );
  const h = totalMs / 3600000;
  if (h === 0) return '0h';
  return h % 1 === 0 ? `${h}h` : `${h.toFixed(1)}h`;
}

const SECTION_LABELS: Record<BookingTab, string> = {
  [BookingTab.UPCOMING]: 'NEXT 30 DAYS',
  [BookingTab.PAST]: 'LAST 30 DAYS',
  [BookingTab.CANCELED]: 'ALL CANCELED',
};

const AllBookings: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<BookingTab>(BookingTab.UPCOMING);
  const [allBookings, setAllBookings] = useState<BookingDetailsDto[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<string | null>(null);
  const [cancellingBookingId, setCancellingBookingId] = useState<string | null>(null);
  const [isCheckingIn] = useState(false);

  // Ref to track the active tab inside async callbacks without stale closures
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  const hasMore = allBookings.length < total;
  const hoursDisplay = useMemo(() => computeHours(allBookings), [allBookings]);
  const completedCount = useMemo(
    () => allBookings.filter((b) => b.status === 'completed').length,
    [allBookings],
  );

  // Only batch-fetch sponsorships for upcoming bookings — past/cancelled use a
  // snapshotted sponsorName stored on the booking at status-transition time (Phase 286).
  const calendarEntries = useMemo(
    () =>
      activeTab === BookingTab.UPCOMING
        ? [
            ...new Map(
              allBookings
                .filter((b) => Boolean(b.calendarId))
                .map((b) => [b.calendarId, { calendarId: b.calendarId, locationId: b.locationId }]),
            ).values(),
          ]
        : [],
    [allBookings, activeTab],
  );
  const { byCalendarId } = useCalendarSponsorships(calendarEntries);

  const getSponsorship = (booking: BookingDetailsDto) => {
    if (activeTab === BookingTab.UPCOMING) {
      // Live sponsorship for upcoming bookings (batch-fetched, Phase 285)
      const resolved = byCalendarId[booking.calendarId];
      return resolved?.isEnabled ? { sponsorName: resolved.sponsorName } : null;
    }
    // Past/cancelled — use snapshotted name captured at status-transition (Phase 286)
    return booking.snapshotSponsorName ? { sponsorName: booking.snapshotSponsorName } : null;
  };

  const fetchPage = useCallback(async (tab: BookingTab, page: number, isFirst: boolean) => {
    if (isFirst) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }

    try {
      const result = await BookingService.getAllBookings(tab, page, PAGE_SIZE);
      // Discard stale responses if the tab changed while fetching
      if (activeTabRef.current !== tab) return;

      if (isFirst) {
        setAllBookings(result.data);
        setTotal(result.total);
        setTotalPages(result.totalPages);
        setCurrentPage(1);
      } else {
        setAllBookings((prev) => [...prev, ...result.data]);
        setCurrentPage(page);
      }
    } catch (err: any) {
      if (activeTabRef.current !== tab) return;
      logger.error('Error fetching bookings:', err);
      setError(err.message || 'Failed to fetch bookings');
      if (isFirst) setAllBookings([]);
    } finally {
      if (activeTabRef.current !== tab) return;
      if (isFirst) setLoading(false);
      else setLoadingMore(false);
    }
  }, []);

  // Reset and reload on tab change
  useEffect(() => {
    setAllBookings([]);
    setTotal(0);
    setTotalPages(1);
    setCurrentPage(1);
    fetchPage(activeTab, 1, true);
  }, [activeTab, fetchPage]);

  const handleLoadMore = useCallback(async () => {
    if (!hasMore || loadingMore || loading) return;
    const nextPage = currentPage + 1;
    if (nextPage > totalPages) return;
    await fetchPage(activeTab, nextPage, false);
  }, [hasMore, loadingMore, loading, currentPage, totalPages, activeTab, fetchPage]);

  const handleBookingCancel = (bookingId: string) => {
    setBookingToCancel(bookingId);
    setCancelDialogOpen(true);
  };

  const handleBookingReschedule = (bookingId: string) => {
    const booking = allBookings.find((b) => (b._id || b.id) === bookingId);
    if (!booking) return;
    navigate('/booking-slots', {
      state: {
        bookingType: {
          facilityType: booking.facility as FacilityType,
          title: facilityTypeTitles[booking.facility as FacilityType] || booking.facility,
          price: 0,
        },
        selectedLocation: {
          ...(booking.location || {}),
          timezone: booking.timezone,
        },
        rescheduleBookingId: booking.id || booking._id,
        rescheduleRoomId: booking.assignedRooms?.[0]?.id,
      },
    });
  };

  const handleCheckInBooking = (bookingId: string) => {
    if (isCheckingIn) return;
    const booking = allBookings.find((b) => (b._id || b.id) === bookingId);
    // TEMP: bypass location verification — go directly to open gate
    navigate('/access-control/open-gate', {
      state: {
        bookingId,
        facilityName: booking?.facility ?? undefined,
        facilityType: booking?.facility,
      },
    });
  };

  const handleConfirmCancel = async () => {
    if (!bookingToCancel || cancellingBookingId) return;
    const idToCancel = bookingToCancel;
    setCancelDialogOpen(false);
    setBookingToCancel(null);
    setCancellingBookingId(idToCancel);
    try {
      await BookingService.cancelBooking(idToCancel);
      // Remove it from the list immediately instead of refetching page 1 —
      // a refetch can still see the pre-cancel state if the backend read
      // hasn't caught up yet, which is why the row used to only disappear
      // after navigating away and back.
      setAllBookings((prev) => prev.filter((b) => (b._id || b.id) !== idToCancel));
      setTotal((prev) => Math.max(0, prev - 1));
      // Same uid format used when the booking was (maybe) added to the
      // calendar — native no-ops gracefully if it never created one.
      triggerNativeRemoveFromCalendar(`${idToCancel}@teebox`);
    } catch (err: any) {
      logger.error('Failed to cancel booking:', err);
    } finally {
      setCancellingBookingId(null);
    }
  };

  const renderBooking = (booking: BookingDetailsDto) => {
    const isActive = booking.status === 'active';
    const started = hasBookingStarted(booking.startTime);
    const canAct = isActive && !started;
    return (
      <SBookingCard
        key={booking._id || booking.id}
        booking={booking}
        sponsorship={getSponsorship(booking)}
        onCancel={canAct ? handleBookingCancel : undefined}
        onReschedule={canAct ? handleBookingReschedule : undefined}
        onCheckIn={handleCheckInBooking}
        isCancelling={cancellingBookingId === (booking._id || booking.id)}
        isCheckingIn={isCheckingIn}
      />
    );
  };

  return (
    <div className="ab-container">
      {/* Header */}
      <div className="ab-header">
        <SBack onClick={() => navigate(-1)} />
        <h1 className="ab-header__title">Sessions</h1>
        <ThemeToggle />
      </div>

      {/* Segmented tabs */}
      <STabs activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Summary — shown as soon as total is known (even before all pages load) */}
      {!loading && !error && total > 0 && (
        <div className="ab-summary">
          <span className="ab-summary__label">{SECTION_LABELS[activeTab]}</span>
          {activeTab === BookingTab.PAST ? (
            <div className="ab-summary__count">
              {completedCount} completed {completedCount === 1 ? 'session' : 'sessions'}
              <span className="ab-summary__duration">· {hoursDisplay}</span>
            </div>
          ) : (
            <div className="ab-summary__count">
              {total} {total === 1 ? 'session' : 'sessions'}
            </div>
          )}
        </div>
      )}

      {/* Initial load */}
      {loading && (
        <div className="ab-state">
          <SLoader />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="ab-state ab-state--error">
          <p>{error}</p>
          <button className="ab-retry" onClick={() => fetchPage(activeTab, 1, true)}>
            Retry
          </button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && allBookings.length === 0 && (
        <div className="ab-state ab-state--empty">
          <p>No {activeTab} sessions</p>
        </div>
      )}

      {/* Infinite scroll list */}
      {!loading && !error && allBookings.length > 0 && (
        <div className="ab-list">
          <SInfiniteScroll
            data={allBookings}
            hasMore={hasMore}
            isLoading={loadingMore}
            onLoadMore={handleLoadMore}
            renderItem={(booking) => renderBooking(booking)}
            gap={14}
          />
        </div>
      )}

      <SDialog
        isOpen={cancelDialogOpen}
        onClose={() => { setCancelDialogOpen(false); setBookingToCancel(null); }}
        title="Cancel this session?"
        onConfirm={handleConfirmCancel}
        confirmText="Yes, cancel"
        cancelText="Keep it"
      />
    </div>
  );
};

export default AllBookings;
